import { useState, useEffect, useCallback } from 'react';
import { apiClient, API_CONFIG, API_ENDPOINTS } from '../config/api';
import { uploadAttachments } from '../utils/attachmentUpload';
import type {
  InquiryRequest,
  InquiryResponse,
  InquiryListItem,
} from '../types/report';

export function useSubmitInquiry() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (request: InquiryRequest): Promise<InquiryResponse['result'] | null> => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');
      const res = await fetch(`${API_CONFIG.baseURL}${API_ENDPOINTS.INQUIRY.SUBMIT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(userId && { KEY_USER_ID: userId }),
        },
        credentials: 'include',
        body: JSON.stringify({
          category: request.category,
          title: request.title,
          content: request.content,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error?.message ?? '문의 접수에 실패했습니다.');
      }

      const data = await res.json();
      const result = data.result;
      const resultId: number | undefined = result?.inquiryId ?? result?.id;

      // 2단계: 첨부파일 업로드
      if (request.attachments && request.attachments.length > 0 && resultId) {
        try {
          await uploadAttachments('INQUIRY', resultId, request.attachments);
        } catch (uploadErr) {
          console.warn('첨부파일 업로드 실패:', uploadErr);
          // 본문은 이미 제출되었으므로 계속 진행
        }
      }

      return result;
    } catch (err: any) {
      setError(err.message ?? '문의 접수에 실패했습니다.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

export function useInquiryList() {
  const [inquiries, setInquiries] = useState<InquiryListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .get<{ result: { inquiries: InquiryListItem[] } }>(API_ENDPOINTS.INQUIRY.LIST)
      .then((res) => {
        if (!cancelled) setInquiries(res.result.inquiries ?? []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '문의 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [version]);

  return { inquiries, loading, error, refetch };
}

export function useInquiryDetail(id: number | null) {
  const [inquiry, setInquiry] = useState<InquiryResponse['result'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (id === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .get<{ result: InquiryResponse['result'] }>(API_ENDPOINTS.INQUIRY.DETAIL(id))
      .then((res) => {
        if (!cancelled) setInquiry(res.result);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '문의 상세를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, version]);

  return { inquiry, loading, error, refetch };
}

export function useReopenInquiry() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reopen = useCallback(async (id: number, content: string): Promise<{ commentId: number } | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post<{ result: { commentId: number } }>(
        API_ENDPOINTS.INQUIRY.REOPEN(id),
        { content }
      );
      return res.result;
    } catch (err: any) {
      setError(err.message ?? '추가 문의 접수에 실패했습니다.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { reopen, loading, error };
}
